//app-routes.tsx

import { useState, useEffect, useContext } from "react";
import type { JSX } from "react";
import {
  useHistory,
  Route,
  Redirect,
  Switch,
  useLocation,
} from "react-router-dom";
import { EmailVerificationPage } from "../pages/email-verification-page";
import { LandingPage } from "../pages/landing-page";
import { LogInPage } from "../pages/log-In-page";
import { ForgotPassword } from "../pages/forgot-password-page";
import { ResetPasswordPage } from "../pages/reset-password-page";
import { SignUpPage } from "../pages/sign-up-page";
import { InitialSetupPage } from "../pages/initial-setup-page";
import HomePage from "../pages/home-page";
import AdminHomePage from "../pages/admin-home-page";
import { VerifyEmailPage } from "../pages/verify-email-page";
import React from "react";
import { Grower } from "../models/grower";
import { User } from "../models/user";
import { usersApi } from "../services/api/users";
import { sessionsApi } from "../services/api/sessions";
import { billingApi } from "../services/api/billing";
import LoadingBackdrop from "./loading-backdrop";
import axios, { AxiosError } from "axios";
import { SubscriptionPastDueDialog } from "./sub-past-due-dialog";
import {
  SubscriptionErrorDialog,
  SubscriptionActions,
} from "./subscription-error-dialog";
import * as Sentry from "@sentry/react";
import { sentryDSN } from "..";
import mixpanel from "mixpanel-browser";
import { subscriptionsApi } from "../services/api/subscriptions";
import { EmailChangePage } from "../pages/email-change-page";
import { Settings, WeekdayNumbers } from "luxon";
import { ReleaseChecker } from "./release-checker";
import { useRef } from "react";
import SupportPage from "../pages/support-page";
import DynamicSupportArticlePage from "../pages/dynamic-support-article-page";
import DynamicSupportCategoryPage from "../pages/dynamic-support-category-page";
import SupportAdminPage from "../pages/support-admin-page";
import { SupportProvider } from "../contexts/support-context";
import ScrollToTop from "../components/scroll-to-top";
import MushroomHomePage from "../pages/mushroom-home-page";
import MushroomGuestAccessPage from "../pages/mushroom-guest-access-page";
import {
  isMushroomGuestActive,
  isMushroomGuestEnabled,
  clearMushroomGuestSession,
} from "../mushroom/guest/mushroom-guest-session";
import { MushroomReadOnlyProvider } from "../mushroom/review/mushroom-readonly-context";
import { AcceptInvitePage } from "../pages/accept-invite-page";
import AdminReferralsPage from "../pages/admin-referrals-page";

interface State {
  isLoggedIn: boolean;
  isSetup: boolean;
  from?: string;
  context?: {
    grower?: Grower;
    user: User;
  };
}

export const ErrorContext = React.createContext({
  handleError: (error: any) => {
    console.error(error);
  },
});

const AnonymousRoute = (props: {
  children: JSX.Element;
  state: State;
  exact?: boolean;
  path: string;
}) => {
  return (
    <Route exact={props.exact} path={props.path}>
      {props.state.isLoggedIn ? (
        <Redirect
          to={
            props.state.isSetup ? props.state.from || "/home" : "/initial-setup"
          }
        />
      ) : (
        <div>{props.children}</div>
      )}
    </Route>
  );
};

const AuthenticatedRoute = (props: {
  children: JSX.Element;
  state: State;
  exact?: boolean;
  path: string;
}) => (
  <Route
    exact={props.exact}
    path={props.path}
    render={({ location }) =>
      props.state.isLoggedIn ? (
        props.children
      ) : (
        <Redirect
          to={{
            pathname: "/log-in",
            state: { from: location.pathname },
          }}
        />
      )
    }
  />
);

export const UserContext = React.createContext<{
  user: User;
  grower?: Grower;
  state: State;
  setState: (state: State) => void;
}>(undefined!);

enum ForbiddenReasons {
  Subscription = "subscription",
}

export function AppRoutes() {
  const [state, setState] = useState<State | null>(null);
  const [busy, setBusy] = useState(false);
  const history = useHistory();
  const [openSubscriptionErrorDialog, setOpenSubscriptionErrorDialog] =
    useState(false);
  const [openSubscriptionPastDueDialog, setOpenSubscriptionPastDueDialog] =
    useState(false);
  const [subscriptionErrorGrower, setSubscriptionErrorGrower] = useState<
    Grower | undefined
  >();
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const user = useContext(UserContext);
  const location = useLocation();

  const handleError = (error: any) => {
    console.error(error);

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<{
        Reason?: string;
        Data?: Grower;
        message?: string;
        error?: string;
      }>;
      console.log("Axios response data:", error.response?.data);
      const resp = axiosError.response;

      if (resp?.data && typeof resp.data !== "string") {
        if (resp.data.error) {
          alert(resp.data.error);
          return;
        }

        if (resp.data.message) {
          alert(resp.data.message);
          return;
        }
      }

      if (!resp) {
        // Network error
        alert(
          "Sorry, something went wrong.  Check your network connection and then try again.",
        );
        return;
      } else if (resp.status === 401) {
        setState({
          isLoggedIn: false,
          isSetup: false,
          context: undefined,
          from: location.pathname,
        });
        return;
      } else if (resp.status === 403) {
        const msg = resp.data.message || "";

        if (
          typeof msg === "string" &&
          msg.toLowerCase().includes("access") &&
          msg.toLowerCase().includes("removed")
        ) {
          sessionsApi.end().catch(() => {
            // ignore
          });

          alert(msg);

          setState({
            isLoggedIn: false,
            isSetup: false,
            context: undefined,
            from: location.pathname,
          });

          return;
        }

        switch (resp.data.Reason) {
          case ForbiddenReasons.Subscription:
            setSubscriptionErrorGrower(resp.data.Data as Grower);
            setOpenSubscriptionErrorDialog(true);
            return;
          default:
            alert(
              resp.data.message || "Sorry, you don't have access to do that.",
            );
            return;
        }
      } else if (resp.status === 409) {
        alert(
          resp.data.message ||
            "Sorry, the operation failed because it would create a conflict.  If you're trying to delete something, make sure it is not in use elsewhere.",
        );
        return;
      } else if (resp.status >= 500) {
        if (typeof resp.data === "string") {
          alert(resp.data);
          return;
        }

        if (resp.data.error) {
          alert(resp.data.error);
          return;
        }

        if (resp.data.message) {
          alert(resp.data.message);
          return;
        }

        if (process.env.NODE_ENV === "production") {
          // Server errors are captured there and here we'll show a report
          // dialog and link it to the backend error event.  Note, this report
          // is associated with the backend DSN.
          const eventId = resp.headers["x-sentry-event-id"];

          Sentry.showReportDialog({
            dsn: "https://36917a9923684448ae2708697943317c@o1060572.ingest.sentry.io/6052365",
            eventId,
            user: user
              ? {
                  name: user.user.Name,
                  email: user.user.Email,
                }
              : undefined,
          });
          return;
        }
      }
    }

    if (process.env.NODE_ENV === "production") {
      // Any other Axios error will be captured and a report dialog will be shown
      // and associated with the error event.

      const eventId = Sentry.captureException(error);

      Sentry.showReportDialog({
        dsn: sentryDSN,
        eventId,
        user: user
          ? {
              name: user.user.Name,
              email: user.user.Email,
            }
          : undefined,
      });

      return;
    }

    // Sentry is not enabled
    alert(error);
  };

  const doSubscriptionAction = async (action: SubscriptionActions) => {
    if (!isMounted.current) return;
    setBusy(true);
    if (isMounted.current) setOpenSubscriptionErrorDialog(false);

    try {
      const { url } = await subscriptionsApi.post({
        action: action.toString(),
      });
      if (!isMounted.current) return;

      if (url) {
        window.location.assign(url);
      } else {
        window.location.reload();
      }
    } catch (error) {
      if (isMounted.current) handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const goToBillingPortal = async () => {
    if (!isMounted.current) return;
    setBusy(true);

    try {
      const session = await billingApi.createPortalSession();
      if (!isMounted.current) return;

      window.location.assign(session.Url);
    } catch (error) {
      if (isMounted.current) handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const handleSubscriptionErrorDialogOnClickGoToBillingPortal = async () => {
    setOpenSubscriptionErrorDialog(false);
    goToBillingPortal();
  };

  const mixpanelIdentifyUser = (user: User) => {
    mixpanel.identify(user.id.toString());
    mixpanel.people.set({
      $email: user.Email,
      $name: user.Name,
      workflow: user.Growers.length > 0 ? user.Growers[0].Workflow : undefined,
    });
  };

  const setLuxonDefaultWeekSettings = (grower: Grower) => {
    let firstDay: WeekdayNumbers;

    switch (grower.StartWeekOn) {
      case "Monday":
        firstDay = 1;
        break;
      case "Sunday":
        firstDay = 7;
        break;
      case "Saturday":
        firstDay = 6;
        break;
      default:
        throw new Error("Invalid StartWeekOn value");
    }

    Settings.defaultWeekSettings = {
      firstDay: firstDay,
      minimalDays: 4,
      weekend: [6, 7],
    };
  };

  useEffect(() => {
    async function getState() {
      try {
        const user = await loadUser();
        if (!isMounted.current) return;

        Sentry.setUser({ email: user.Email });
        window.Frill("identify", { email: user.Email, name: user.Name });
        mixpanelIdentifyUser(user);

        if (user.Growers.length === 0) return;

        setLuxonDefaultWeekSettings(user.Growers[0]);

        const isPastDue =
          user.Growers[0].StripeSubscriptionStatus === "past_due";

        if (isPastDue && isMounted.current) {
          setOpenSubscriptionPastDueDialog(true);
        }
      } catch (error) {
        if (!isMounted.current) return;
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 401) {
            if (isMounted.current) {
              setState({
                isLoggedIn: false,
                isSetup: false,
                context: undefined,
              });
            }
            return;
          }
        }
        if (isMounted.current) handleError(error);
      }
    }

    getState();
  }, []);

  const handleLogIn = async (from?: string) => {
    try {
      const user = await loadUser(from);
      if (!isMounted.current) return;

      Sentry.setUser({ email: user.Email });
      window.Frill("identify", { email: user.Email, name: user.Name });
      mixpanelIdentifyUser(user);
      mixpanel.track("Log In");

      if (user.Growers.length !== 0 && isMounted.current) {
        setLuxonDefaultWeekSettings(user.Growers[0]);
      }
    } catch (error) {
      if (isMounted.current) handleError(error);
    }
  };

  const handleSetup = async () => {
    try {
      await loadUser();
      if (!isMounted.current) return;
    } catch (error) {
      if (isMounted.current) handleError(error);
    }
  };

  const handleSignUp = async () => {
    try {
      const user = await loadUser();
      if (!isMounted.current) return;

      Sentry.setUser({ email: user.Email });

      mixpanelIdentifyUser(user);
      mixpanel.track("Sign Up");
      window.Frill("identify", { email: user.Email, name: user.Name });

      if (LeadDyno) {
        LeadDyno.recordLead(user.Email);
      }
    } catch (error) {
      if (isMounted.current) handleError(error);
    }
  };

  const handleLogOut = async () => {
    try {
      await sessionsApi.end();

      if (!isMounted.current) return;

      setState({
        isLoggedIn: false,
        isSetup: false,
        context: undefined,
      });

      Sentry.setUser(null);
      window.Frill("unidentify");
      mixpanel.reset();
    } catch (error) {
      if (isMounted.current) handleError(error);
    }
  };

  const handleEmailVerification = async () => {
    mixpanel.track("Verify Email");

    if (!state?.isLoggedIn) {
      history.replace("/log-in");
      return;
    }

    try {
      await loadUser();
      if (!isMounted.current) return;

      history.replace("/home");
    } catch (error) {
      if (isMounted.current) handleError(error);
    }
  };

  const handleGrowerUpdate = async () => {
    if (!isMounted.current) return;
    setBusy(true);

    try {
      const user = await loadUser();
      if (!isMounted.current) return;

      setLuxonDefaultWeekSettings(user.Growers[0]);
    } catch (error) {
      if (isMounted.current) handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const loadUser = async (from?: string): Promise<User> => {
    const user = await usersApi.get();
    setStateForUser(user, from);
    return user;
  };

  const setStateForUser = (user: User, from?: string): void => {
    const grower = user.Growers.length > 0 ? user.Growers[0] : undefined;

    if (grower) {
      const isNewGrower =
        !grower.Onboarding || Object.keys(grower.Onboarding || {}).length === 0;

      // ✅ Defaults ONLY for new growers
      if (isNewGrower) {
        grower.ShowBatchColors = true;
        grower.Workflow = "advanced";
      }
    }

    setState({
      isLoggedIn: true,
      isSetup: user.Growers.length > 0,
      context: {
        grower,
        user,
      },
      from,
    });
  };

  const handleSubscriptionErrorDialogOnClickLogOut = () => {
    setOpenSubscriptionErrorDialog(false);
    handleLogOut();
  };

  const handleSubscriptionPastDueDialogOnClickClose = () => {
    setOpenSubscriptionPastDueDialog(false);
  };

  const handleSubscriptionPastDueDialogOnClickGoToBillingPortal = () => {
    setOpenSubscriptionPastDueDialog(false);
    goToBillingPortal();
  };

  const handleEmailChanged = async () => {
    try {
      await loadUser();
      if (!isMounted.current) return;

      history.replace("/home/settings");
    } catch (error) {
      if (isMounted.current) handleError(error);
    }
  };

  const isLoading = !state;

  if (isLoading) {
    return (
      <SupportProvider>
        <ErrorContext.Provider value={{ handleError }}>
          <div>
            <LoadingBackdrop open={true} />
            {openSubscriptionErrorDialog && (
              <SubscriptionErrorDialog
                onClickGoToBillingPortal={
                  handleSubscriptionErrorDialogOnClickGoToBillingPortal
                }
                onClickLogOut={handleSubscriptionErrorDialogOnClickLogOut}
                onClickSubscriptionAction={doSubscriptionAction}
                grower={subscriptionErrorGrower}
              />
            )}
            {openSubscriptionPastDueDialog && (
              <SubscriptionPastDueDialog
                onClickClose={handleSubscriptionPastDueDialogOnClickClose}
                onClickGoToBillingPortal={
                  handleSubscriptionPastDueDialogOnClickGoToBillingPortal
                }
              />
            )}
          </div>
          <LoadingBackdrop open={busy} />
        </ErrorContext.Provider>
      </SupportProvider>
    );
  }

  return (
    <SupportProvider>
      <ErrorContext.Provider value={{ handleError }}>
        <div>
          {state && (
            <>
              <ScrollToTop />
              <Switch>
                <Route path="/email-verification/:id/:token">
                  <EmailVerificationPage onComplete={handleEmailVerification} />
                </Route>
                <AnonymousRoute state={state} exact path="/">
                  <LandingPage />
                </AnonymousRoute>
                <AnonymousRoute state={state} path="/log-in">
                  <LogInPage onLoggedIn={handleLogIn} />
                </AnonymousRoute>
                <AnonymousRoute state={state} path="/forgot-password">
                  <ForgotPassword />
                </AnonymousRoute>
                <AnonymousRoute state={state} path="/reset-password">
                  <ResetPasswordPage />
                </AnonymousRoute>
                <AnonymousRoute state={state} path="/sign-up">
                  <SignUpPage onSignUp={handleSignUp} />
                </AnonymousRoute>
                <Route path="/accept-invite">
                  <AcceptInvitePage />
                </Route>
                <AuthenticatedRoute state={state} path="/initial-setup">
                  {state.isSetup ? (
                    <Redirect to="/home" />
                  ) : (
                    <InitialSetupPage onSetup={handleSetup} />
                  )}
                </AuthenticatedRoute>
                <AuthenticatedRoute state={state} path="/admin/referrals">
                  <AdminReferralsPage />
                </AuthenticatedRoute>
                <AuthenticatedRoute state={state} path="/admin">
                  <AdminHomePage />
                </AuthenticatedRoute>
                <Route path="/mushroom/guest">
                  <MushroomGuestAccessPage />
                </Route>
                <AuthenticatedRoute state={state} path="/home">
                  {state.context && state.isSetup ? (
                    <UserContext.Provider
                      value={{
                        user: state.context.user,
                        grower: state.context.grower,
                        state,
                        setState: setState,
                      }}
                    >
                      <HomePage
                        onLogOut={handleLogOut}
                        onGrowerUpdated={handleGrowerUpdate}
                      />
                    </UserContext.Provider>
                  ) : (
                    <Redirect to="/initial-setup" />
                  )}
                </AuthenticatedRoute>

                <Route path="/mushroom/home">
                  {state.context && state.isSetup ? (
                    <UserContext.Provider
                      value={{
                        user: state.context.user,
                        grower: state.context.grower,
                        state,
                        setState: setState,
                      }}
                    >
                      <MushroomHomePage
                        onLogOut={handleLogOut}
                        onGrowerUpdated={handleGrowerUpdate}
                      />
                    </UserContext.Provider>
                  ) : isMushroomGuestActive() ? (
                    <UserContext.Provider
                      value={{
                        user: {
                          id: 0,
                          Name: "Guest Reviewer",
                          Email: "guest@staging",
                          EmailVerified: true,
                          Growers: [
                            {
                              id: "guest",
                              Name: "Guest",
                              UnitSystem: "metric",
                              CountryISO: "CA",
                              StripeSubscriptionStatus: "trialing",
                              Plan: "business",
                              Onboarding: {},
                              Workflow: "advanced",
                              TimeZone: "America/Vancouver",
                              StartWeekOn: "Monday",
                            } as Grower,
                          ],
                        } as unknown as User,
                        grower: {
                          id: "guest",
                          Name: "Guest",
                          UnitSystem: "metric",
                          CountryISO: "CA",
                          StripeSubscriptionStatus: "trialing",
                          Plan: "business",
                          Onboarding: {},
                          Workflow: "advanced",
                          TimeZone: "America/Vancouver",
                          StartWeekOn: "Monday",
                        } as unknown as Grower,
                        state,
                        setState: setState,
                      }}
                    >
                      <MushroomReadOnlyProvider>
                        <MushroomHomePage
                          onLogOut={() => {
                            clearMushroomGuestSession();
                            window.location.href = "/mushroom/guest";
                          }}
                          onGrowerUpdated={() => {}}
                        />
                      </MushroomReadOnlyProvider>
                    </UserContext.Provider>
                  ) : isMushroomGuestEnabled() ? (
                    <Redirect to="/mushroom/guest" />
                  ) : (
                    <Redirect to="/initial-setup" />
                  )}
                </Route>

                {state.context && state.isSetup && (
                  <AuthenticatedRoute state={state} path="/support-admin">
                    <SupportAdminPage />
                  </AuthenticatedRoute>
                )}
                <AuthenticatedRoute state={state} path="/verify-email">
                  {(state.context &&
                    (state.context?.user.EmailVerified ? (
                      <Redirect to="/home" />
                    ) : (
                      <UserContext.Provider
                        value={{
                          user: state.context.user,
                          grower: state.context.grower,
                          state,
                          setState,
                        }}
                      >
                        <VerifyEmailPage />
                      </UserContext.Provider>
                    ))) || <div>Error</div>}
                </AuthenticatedRoute>
                <AuthenticatedRoute state={state} path="/email-change/:token">
                  <EmailChangePage onComplete={handleEmailChanged} />
                </AuthenticatedRoute>
              </Switch>
              <ReleaseChecker />
            </>
          )}
          {openSubscriptionErrorDialog && (
            <SubscriptionErrorDialog
              onClickGoToBillingPortal={
                handleSubscriptionErrorDialogOnClickGoToBillingPortal
              }
              onClickLogOut={handleSubscriptionErrorDialogOnClickLogOut}
              onClickSubscriptionAction={doSubscriptionAction}
              grower={subscriptionErrorGrower}
            />
          )}
          {openSubscriptionPastDueDialog && (
            <SubscriptionPastDueDialog
              onClickClose={handleSubscriptionPastDueDialogOnClickClose}
              onClickGoToBillingPortal={
                handleSubscriptionPastDueDialogOnClickGoToBillingPortal
              }
            />
          )}
        </div>
        <LoadingBackdrop open={busy} />
      </ErrorContext.Provider>
    </SupportProvider>
  );
}
